import { useNavigate } from 'react-router';
import { casePath, opensLabNotes } from '@/domain';
import type { Notification } from '@/application/ports/NotificationRepository';
import { useFindCase } from '@/application/usecases/useKits';
import { useMarkNotificationRead } from '@/application/usecases/useNotifications';
import { useSession } from '@/application/usecases/useSession';
import { NOTICE_PARAM, useCaseInPlace } from '@/features/orders/useCaseInPlace';
import { paymentSummaryPath } from '@/features/payments/paymentPaths';

/**
 * What tapping a notification does: it is marked read, and the case it names
 * opens. The request of 7 October 2026, section 2.
 *
 * In place, over whatever screen the reader is on, in the one case view every
 * list opens a case in, for whoever reads cases as orders; the address says
 * which, and Back closes it. A bench role does not read orders: the bell names
 * the job order, so its tray is looked up and the job view opens, on its Lab
 * Notes for a message or a case given to them, where the conversation is.
 *
 * Marking read on the tap rather than on drawing is deliberate: a badge that
 * cleared itself the moment somebody glanced at the list would lose the one
 * thing it is for.
 */
export function useOpenNotification(onOpened?: () => void): (notification: Notification) => void {
  const navigate = useNavigate();
  const session = useSession();
  const find = useFindCase();
  const markRead = useMarkNotificationRead();
  const place = useCaseInPlace(NOTICE_PARAM);

  return (notification) => {
    if (!notification.read) markRead.mutate(notification.id);
    if (notification.target === null) return;

    const jobOrderNo = notification.target;
    const views = session.data?.views ?? [];
    onOpened?.();

    // A payment to follow up opens where it is followed up: the case's payment
    // summary, for whoever holds Payments. The request of 8 October 2026 (fourth).
    if (notification.type === 'review.payment' && views.includes('payments')) {
      void navigate(paymentSummaryPath(jobOrderNo));
      return;
    }

    if (place.mayOpen) {
      place.openNumber(jobOrderNo);
      return;
    }

    // Awaited rather than handed callbacks: the list may have just closed, and a
    // mutation's callbacks are dropped once the component that called it is gone.
    find
      .mutateAsync(jobOrderNo)
      .then(({ kitId }) => {
        const job = casePath(views, { jobOrderNo, kitId }, 'kit');
        if (job === null) return;
        // From the bell: the job view asks first if the case has moved on from them.
        void navigate(opensLabNotes(notification.type) && job.startsWith('/queue/') ? `${job}?notes=1` : job, {
          state: { fromNotification: true },
        });
      })
      .catch(() => {
        // A case that has gone since the bell rang: nothing to open.
      });
  };
}
